import React, { createContext, useState } from 'react';
import { CognitoUser, CognitoUserSession } from 'amazon-cognito-identity-js';
import { Auth } from '@aws-amplify/auth';
import { actionDispatcher } from '../utils/ActionDispatchers';
import { supportedLocales } from '../locales/supportedLocales';
import { getSamlAuthToken } from '../services/saml-api';
import messageFilter from '../utils/message-filter';
import { getTimeZoneAreaLocation } from '../utils/timezone';
import { gotoSignIn } from '../utils/helpers';

interface keyValuePair {
  [key: string]: any;
}

type ContextProps = {
  idToken: string;
  cognitoUser: CognitoUser | Record<string, unknown>;
  isSAMLAuthenticated: boolean;
  hasRequestErrors: keyValuePair;
  /** SAML: Set Auth Status */
  setIsSAMLAuthentication: (config: {
    authToken: string;
    colorMode?: string;
    accountID?: string;
    redirectTo?: string;
  }) => void;
  isAuthenticated: boolean;
  setIsAuthenticating: React.Dispatch<React.SetStateAction<boolean>>;
  isAuthenticating: boolean;
  setIsAuthenticated: (b: boolean) => void;
  getUserAttributes: () => Promise<Record<string, string | unknown>>;
  eventDistributor: { dispatch: (ReduxAction) => void };
  setEventDistributor: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
  setCognitoUser: (cognitoUser: CognitoUser | Record<string, unknown>) => void;
  getCurrentSession: () => Promise<void>;
  setAuthStatus: (authenticatedUser: CognitoUserSession) => Promise<void>;
};

const AuthContext = createContext<ContextProps>(undefined as never);

const { Provider } = AuthContext;

const AuthProvider = ({ children }) => {
  // Cognito
  const [idToken, setIdToken] = useState('');
  const storageShellState = sessionStorage.getItem('C1C_Shell_State');
  const getAuthState = storageShellState ? JSON.parse(storageShellState).c1Common.loggedIn : false;
  const [isAuthenticated, setIsAuthenticated] = useState(() => getAuthState);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [cognitoUser, setCognitoUser] = useState({});
  const [eventDistributor, setEventDistributor] = useState(children.props.globalEventDistributor);
  const [hasRequestErrors, setHasRequestErrors] = useState({
    error: '',
    message: '',
  });
  const getCurrentColorMode = () => {
    const storedColorMode = localStorage.getItem('C1C_THEME_COLOR_MODE');
    if (storedColorMode) {
      eventDistributor.dispatch({
        type: 'SET_COLOR_MODE',
        colorMode: storedColorMode,
      });
    }
  };

  // SAML Auth State
  const [isSAMLAuthenticated, setIsSAMLAuthenticated] = useState(false);

  const setIsSAMLAuthentication: ContextProps['setIsSAMLAuthentication'] = async ({
    authToken,
    accountID,
    colorMode,
    redirectTo,
  }) => {
    setIsAuthenticating(true);

    try {
      const {
        access_token = '',
        exp = '',
        locale = '',
        name = 'User',
        timezone = '',
      } = await getSamlAuthToken(authToken);

      if (access_token) {
        const defaultTimeZone = getTimeZoneAreaLocation();
        sessionStorage.setItem('idToken', access_token);
        setIdToken(access_token);

        localStorage.setItem('C1C_USER_TYPE', 'cloudone-backed');
        localStorage.setItem('C1C_AUTH_SAML', 'true');

        // SAML use-case to help differentiate between cognito and SAML
        eventDistributor.dispatch(
          actionDispatcher('ADD_AUTH_DETAILS', 'authDetails', {
            authType: 'saml',
            expires: exp,
          })
        );

        eventDistributor.dispatch(
          actionDispatcher('ADD_USER_INFO', 'userDetails', {
            email: '',
            name,
            timezone: timezone !== '' ? timezone : defaultTimeZone,
          })
        );

        // Set locale
        const storedLocale = localStorage.getItem('C1C_locale');
        const userLocale = locale
          ? locale
          : storedLocale && supportedLocales.includes(storedLocale)
          ? storedLocale
          : 'en';
        localStorage.setItem('C1C_locale', userLocale);
        eventDistributor.dispatch({ type: 'SET_LOCALE', locale: userLocale });

        // Set Color Mode
        getCurrentColorMode();

        // if user is authenticated update authentication
        eventDistributor.dispatch(actionDispatcher('LOGGED_IN', 'loggedIn', true)); // needed once - let all apps know
        setIsSAMLAuthenticated(true);

        // Record SSO user type explicitly
        eventDistributor.dispatch(actionDispatcher('ADD_USER_TYPE', 'userType', 'cloudone-backed'));

        // Request Accounts
        eventDistributor.dispatch(actionDispatcher('FETCH_ACCOUNTS', 'requestAccounts', true));

        // Check for query parameters indicating we need to redirect after login, bypass account selector, change theme
        if (redirectTo) {
          window.sessionStorage.setItem('C1C_REQUEST_URL', decodeURIComponent(redirectTo));
        }
        if (accountID) {
          window.localStorage.setItem('lastAccessedAccountID', accountID);
        }
        if (colorMode) {
          eventDistributor.dispatch(actionDispatcher('SET_COLOR_MODE', 'colorMode', colorMode));
        }

        setIsAuthenticated(true);
      } else {
        throw new Error('Error retrieving SAML Tokens ');
      }
    } catch (error) {
      setIsSAMLAuthenticated(false);
      setIsAuthenticating(false);
      setHasRequestErrors({
        ...hasRequestErrors,
        error: messageFilter(error) || 'Something went wrong, please try again later',
      });
      console.error(error.message);
    }
  };

  /**
   * Cognito: Update Auth Status
   * @param authenticatedUser
   */
  const setAuthStatus = async (authenticatedUser: CognitoUserSession): Promise<void> => {
    setIsAuthenticating(true);
    // Cognito ID Token
    const currentAuthenticatedUser = authenticatedUser?.signInUserSession
      ? authenticatedUser?.signInUserSession
      : authenticatedUser;

    const idToken = currentAuthenticatedUser.getIdToken().getJwtToken();
    sessionStorage.setItem('idToken', idToken);
    setIdToken(idToken);

    // Set user info
    try {
      // Force bypassCache for:
      // - reset MFA. After signing in again, we get old values for preferredMFA. Should equal 'NOMFA', but instead is still 'SOFTWARE_TOKEN'
      //   Might be because user pool proxy API make its own calls to change MFA and this library doesn't realize its cache is stale.
      const currentUserDetails = await Auth.currentAuthenticatedUser({ bypassCache: true });
      const {
        attributes: { email, name, locale, zoneinfo, 'custom:hasRecoveryCode': strHasRecoveryCode },
        preferredMFA,
      } = currentUserDetails;
      // Custom attribute (hasRecoveryCode) is returned as a stringified boolean. Convert to proper boolean
      // Custom attribute sent for all users, but only intended for MFA users. Set to null for non-MFA users
      const hasMFAResetCode = preferredMFA !== 'NOMFA' ? strHasRecoveryCode === 'true' : null;

      eventDistributor.dispatch(
        actionDispatcher('ADD_USER_INFO', 'userDetails', { email, name, timezone: zoneinfo })
      );

      eventDistributor.dispatch(actionDispatcher('LOGGED_IN', 'loggedIn', true)); // needed once - let all apps know

      setCognitoUser(currentUserDetails); // Required for MFA

      // Set locale
      const storedLocale = localStorage.getItem('C1C_locale');
      const userLocale = locale
        ? locale
        : storedLocale && supportedLocales.includes(storedLocale)
        ? storedLocale
        : 'en';
      localStorage.setItem('C1C_locale', userLocale);
      eventDistributor.dispatch({ type: 'SET_LOCALE', locale: userLocale });

      // Set Color Mode
      getCurrentColorMode();

      eventDistributor.dispatch({ type: 'TOGGLE_HAS_MFA_RESET_CODE', hasMFAResetCode });
    } catch (e) {
      console.error('Error getting user details: ', e);
    }

    // Setting this value to show ssov2 Page during transition -- needed once - normal login - mfa login
    localStorage.setItem('C1C_USER_TYPE', 'cloudone-backed');

    // Record SSO user type explicitly
    eventDistributor.dispatch(actionDispatcher('ADD_USER_TYPE', 'userType', 'cloudone-backed'));

    // Request Accounts
    eventDistributor.dispatch(actionDispatcher('FETCH_ACCOUNTS', 'requestAccounts', true));

    // Fetch Invitations API call
    eventDistributor.dispatch({ type: 'FETCH_RECEIVED_INVITATIONS', fetchRecInvitations: true });

    setIsAuthenticated(true);
  };

  /**
   * Get User Attributes from Cognito Store
   * @param _
   */
  const getUserAttributes = async (): Promise<Record<string, string | unknown>> => {
    try {
      const currentUserDetails = await Auth.currentAuthenticatedUser();
      const { attributes } = currentUserDetails;
      return { ...attributes };
    } catch (e) {
      console.error('Error getting user attributes ', e);
      return {
        email: undefined,
        name: undefined,
        locale: undefined,
        zoneinfo: undefined,
      };
    }
  };

  /**
   * Cognito: Update Current Session
   * @param authenticatedUser
   */
  const updateCurrentSession = async (authenticatedUser): Promise<void> => {
    setIsAuthenticating(true);
    // Cognito ID Token -
    const currentAuthenticatedUser = authenticatedUser?.signInUserSession
      ? authenticatedUser.signInUserSession
      : authenticatedUser;

    const idToken = currentAuthenticatedUser?.getIdToken()?.getJwtToken();
    sessionStorage.setItem('idToken', idToken);

    setIsAuthenticated(true);
  };

  /**
   * Cognito Get Current Session
   * @param _
   */
  const getCurrentSession = async (): Promise<void> => {
    if (!isSAMLAuthenticated) {
      try {
        const currentSession = await Auth.currentSession();

        if (currentSession && currentSession.isValid()) {
          if (window.location.pathname === '/create-account') {
            await updateCurrentSession(currentSession);
          } else {
            await setAuthStatus(currentSession);
          }
        } else {
          setIsAuthenticating(false);
          return gotoSignIn(eventDistributor);
        }
      } catch (error) {
        if (error && error !== 'No current user') {
          console.error('Error occurred while trying to get current session ', error);
        }
      }

      setIsAuthenticating(false);
    }
  };

  return (
    <Provider
      value={{
        idToken,
        cognitoUser,
        isAuthenticating,
        isSAMLAuthenticated,
        setIsSAMLAuthentication,
        setIsAuthenticating,
        hasRequestErrors,
        isAuthenticated,
        setIsAuthenticated,
        eventDistributor,
        setEventDistributor,
        setCognitoUser,
        getUserAttributes,
        getCurrentSession,
        setAuthStatus,
      }}
    >
      {children}
    </Provider>
  );
};

export { AuthContext, AuthProvider };
